import { getSupabase as getSupabaseNullable } from "../../lib/supabase";
import type { WebhookDelivery } from "../../types";
import type { IOperationResult, IGetAllOptions } from "../types";
import { fromDb } from "./baseService";

function requireSupabase() {
  const client = getSupabaseNullable();
  if (!client) throw new Error("Supabase not initialized. Call initSupabase() first.");
  return client;
}

export class WebhookDeliveryService {
  static async getAll(options?: IGetAllOptions): Promise<IOperationResult<WebhookDelivery[]>> {
    try {
      const supabase = requireSupabase();
      let query = supabase
        .from("webhook_deliveries_enriched")
        .select("*", { count: "exact" });

      if (options?.search) {
        const term = `%${options.search}%`;
        query = query.or(`event_type.ilike.${term},subscription_name.ilike.${term}`);
      }

      if (options?.orderBy?.length) {
        for (const orderSpec of options.orderBy) {
          const [field, dir = "asc"] = orderSpec.split(" ");
          query = query.order(field.replace(/[A-Z]/g, (l) => `_${l.toLowerCase()}`), {
            ascending: dir === "asc",
          });
        }
      } else {
        // Default: newest first
        query = query.order("created_at", { ascending: false });
      }

      if (options?.skip != null && options?.top != null) {
        query = query.range(options.skip, options.skip + options.top - 1);
      } else if (options?.top != null) {
        query = query.limit(options.top);
      }

      const { data, error, count } = await query;
      if (error) return { success: false, error };

      const mapped = (data ?? []).map((row) =>
        fromDb<WebhookDelivery>(row as Record<string, unknown>)
      );
      return { success: true, data: mapped, totalCount: count ?? undefined };
    } catch (error) {
      return { success: false, error };
    }
  }

  static async get(id: string): Promise<IOperationResult<WebhookDelivery>> {
    try {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from("webhook_deliveries_enriched")
        .select("*")
        .eq("id", id)
        .single();

      if (error) return { success: false, error };
      return { success: true, data: fromDb<WebhookDelivery>(data as Record<string, unknown>) };
    } catch (error) {
      return { success: false, error };
    }
  }

  /**
   * Get deliveries for a specific subscription.
   */
  static async getBySubscription(
    subscriptionId: string,
    options?: IGetAllOptions
  ): Promise<IOperationResult<WebhookDelivery[]>> {
    try {
      const supabase = requireSupabase();
      let query = supabase
        .from("webhook_deliveries_enriched")
        .select("*", { count: "exact" })
        .eq("subscription_id", subscriptionId);

      query = query.order("created_at", { ascending: false });

      if (options?.skip != null && options?.top != null) {
        query = query.range(options.skip, options.skip + options.top - 1);
      } else if (options?.top != null) {
        query = query.limit(options.top);
      }

      const { data, error, count } = await query;
      if (error) return { success: false, error };

      const mapped = (data ?? []).map((row) =>
        fromDb<WebhookDelivery>(row as Record<string, unknown>)
      );
      return { success: true, data: mapped, totalCount: count ?? undefined };
    } catch (error) {
      return { success: false, error };
    }
  }

  /**
   * Manually retry a failed delivery (resets attempts, clears next_retry_at).
   */
  static async retry(id: string): Promise<IOperationResult<WebhookDelivery>> {
    try {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from("webhook_deliveries")
        .update({
          attempts: 0,
          status_code: null,
          response_body: null,
          delivered_at: null,
          next_retry_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select()
        .single();

      if (error) return { success: false, error };
      return { success: true, data: fromDb<WebhookDelivery>(data as Record<string, unknown>) };
    } catch (error) {
      return { success: false, error };
    }
  }
}
